import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  RefreshCw,
  AlertCircle,
  Search,
  MapPin,
  ExternalLink,
  Edit2,
  Trash2,
} from "lucide-react";
import StoreForm from "../components/StoreForm";
import EditStoreModal from "../components/EditStoreModal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { getAllStores, createStore, updateStore, deleteStore } from "../services/storeApi";
import { getImageUrl } from "../utils/imageHelper";

const StoreAdmin = () => {
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [deletingStore, setDeletingStore] = useState(null);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedState, setSelectedState] = useState("All States");

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const fetchStores = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getAllStores();
      setStores(response.data || []);
    } catch (error) {
      console.error("Error fetching stores:", error);
      setError("Failed to load stores. Please check your database connection.");
      showToast("Failed to load stores", "error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch stores on mount
  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // Filter stores based on search and filters
  useEffect(() => {
    let filtered = stores;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (store) =>
          store.store_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          store.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          store.address?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // State filter
    if (selectedState !== "All States") {
      filtered = filtered.filter((store) => store.state === selectedState);
    }

    // Sort to show active stores first
    filtered = filtered.sort((a, b) => {
      // Active stores (is_active !== false) come before inactive stores (is_active === false)
      const aActive = a.is_active !== false;
      const bActive = b.is_active !== false;
      
      if (aActive === bActive) return 0; // Keep original order if same status
      return aActive ? -1 : 1; // Active stores first
    });

    setFilteredStores(filtered);
  }, [stores, searchQuery, selectedState]);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleAddStore = async (storeData) => {
    try {
      setIsSubmitting(true);
      const response = await createStore(storeData);
      setStores([response.data, ...stores]);
      setShowAddForm(false);
      showToast("Store created successfully!", "success");
    } catch (error) {
      console.error("Error creating store:", error);
      const errorMsg =
        error.response?.data?.errors?.join(", ") ||
        error.response?.data?.error ||
        "Failed to create store";
      showToast(errorMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditStore = async (storeData) => {
    try {
      setIsSubmitting(true);
      const response = await updateStore(editingStore.id, storeData);
      setStores(stores.map((s) => (s.id === editingStore.id ? response.data : s)));
      setEditingStore(null);
      showToast("Store updated successfully!", "success");
    } catch (error) {
      console.error("Error updating store:", error);
      const errorMsg =
        error.response?.data?.errors?.join(", ") ||
        error.response?.data?.error ||
        "Failed to update store";
      showToast(errorMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteStore = async () => {
    try {
      setIsSubmitting(true);
      await deleteStore(deletingStore.id);
      setStores(stores.filter((s) => s.id !== deletingStore.id));
      setDeletingStore(null);
      showToast("Store deleted successfully!", "success");
    } catch (error) {
      console.error("Error deleting store:", error);
      showToast("Failed to delete store", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get unique states for the filter dropdown
  const states = ["All States", ...new Set(stores.map((s) => s.state).filter(Boolean))];
  const isFilteredView = Boolean(searchQuery || selectedState !== "All States");

  const PaginationRow = ({ filteredCount, totalCount, position = "bottom" }) => (
    <div
      className={`bg-gray-50 px-4 py-2 border-gray-200 flex items-center justify-between text-sm ${
        position === "top" ? "border-b" : "border-t"
      }`}
    >
      <div className="text-sm text-gray-600">
        Showing <span className="font-medium">{filteredCount}</span> of{" "}
        <span className="font-medium">{totalCount}</span> stores
      </div>
      <div className="flex items-center gap-2">
        <button className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
          Previous
        </button>
        <button className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          1
        </button>
        <button className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
          Next
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Top Bar with Search, Actions and Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 sm:p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-xs sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text"
              placeholder="Search stores..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 h-9 border border-gray-200 rounded-lg text-xs bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {states.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>

          <button
            onClick={fetchStores}
            className="h-9 px-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors inline-flex items-center gap-1 text-xs font-medium text-gray-700"
            disabled={isLoading}
          >
            <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Refresh</span>
          </button>

          <button
            onClick={() => setShowAddForm(true)}
            className="h-9 px-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-1 text-xs font-semibold"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">Add Store</span>
          </button>
        </div>
      </div>

      {/* Store Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw size={32} className="animate-spin text-blue-600" />
          </div>
        ) : filteredStores.length === 0 ? (
          <div className="text-center py-8">
            <MapPin size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No stores found</h3>
            <p className="text-gray-600 mb-4">
              {isFilteredView
                ? "Try adjusting your search or filters"
                : "Get started by adding your first store"}
            </p>
            {!isFilteredView && (
              <button
                onClick={() => setShowAddForm(true)}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2 font-medium"
              >
                <Plus size={20} />
                Add First Store
              </button>
            )}
          </div>
        ) : (
          <>
            <PaginationRow
              filteredCount={filteredStores.length}
              totalCount={stores.length}
              position="top"
            />
            <div className="overflow-x-auto text-sm">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left">
                      <input type="checkbox" className="rounded border-gray-300" />
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Branch Code
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Store Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Address
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredStores.map((store) => (
                    <tr key={store.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <input type="checkbox" className="rounded border-gray-300" />
                      </td>
                      <td className="px-4 py-3">
                        {store.image_url ? (
                          <img
                            src={getImageUrl(store.image_url)}
                            alt={store.store_name}
                            className="h-12 w-12 rounded-lg object-cover border border-gray-200 shadow-sm"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-[10px] text-gray-400 bg-gray-50">
                            No image
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-mono text-blue-600 font-semibold">
                          {store.branch_code || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                          {store.store_name}
                        </div>
                        <div className="text-xs text-gray-500">ID: #{store.id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{store.city}</div>
                        <div className="text-xs text-gray-500">{store.state}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div
                          className="text-sm text-gray-600 max-w-xs truncate"
                          title={store.address}
                        >
                          {store.address}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          store.is_active === false
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                        }`}>
                          {store.is_active === false ? "Inactive" : "Active"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {store.google_map_link && (
                            <button
                              onClick={() => window.open(store.google_map_link, "_blank")}
                              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View on Map"
                            >
                              <ExternalLink size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => setEditingStore(store)}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => setDeletingStore(store)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationRow
              filteredCount={filteredStores.length}
              totalCount={stores.length}
              position="bottom"
            />
          </>
        )}
      </div>

      {/* Add Store Form */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <StoreForm
            onSubmit={handleAddStore}
            onCancel={() => setShowAddForm(false)}
            isLoading={isSubmitting}
          />
        </div>
      )}

      {/* Edit Store Modal */}
      {editingStore && (
        <EditStoreModal
          store={editingStore}
          onSubmit={handleEditStore}
          onCancel={() => setEditingStore(null)}
          isLoading={isSubmitting}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingStore && (
        <DeleteConfirmationModal
          store={deletingStore}
          onConfirm={handleDeleteStore}
          onCancel={() => setDeletingStore(null)}
          isLoading={isSubmitting}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div
            className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 ${
              toast.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
            }`}
          >
            {toast.type === "success" ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <AlertCircle size={20} />
            )}
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreAdmin;
